/**
 * Analytics tracker stub.
 * Buffers events locally and forwards them to a configurable endpoint when set.
 * Default: console.debug only. Wire to Supabase edge function `track-event` in production
 * by setting window.__NEOVAA_TRACK_ENDPOINT__ before init.
 */

import type { TrackingEvent } from "@/types";

declare global {
  interface Window {
    __NEOVAA_TRACK_ENDPOINT__?: string;
    __NEOVAA_TRACK_DEBUG__?: boolean;
  }
}

const buffer: TrackingEvent[] = [];
const BUFFER_LIMIT = 20;
let sessionId: string | null = null;
let endpoint: string | null = null;

function getSessionId(): string {
  if (sessionId) return sessionId;
  const stored = sessionStorage.getItem("neovaa-session-id");
  if (stored) {
    sessionId = stored;
    return stored;
  }
  const fresh = crypto.randomUUID();
  sessionStorage.setItem("neovaa-session-id", fresh);
  sessionId = fresh;
  return fresh;
}

export function initTracker(): void {
  endpoint = window.__NEOVAA_TRACK_ENDPOINT__ ?? null;
  // Flush on page hide
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      void flush();
    }
  });
}

export function trackEvent(name: string, properties?: Record<string, unknown>): void {
  const ev: TrackingEvent = {
    name,
    properties: { ...properties, sessionId: getSessionId() },
    timestamp: Date.now(),
  };
  if (window.__NEOVAA_TRACK_DEBUG__) {
    console.debug("[track]", name, properties);
  }
  buffer.push(ev);
  if (buffer.length >= BUFFER_LIMIT) {
    void flush();
  }
}

export async function flush(): Promise<void> {
  if (!endpoint || buffer.length === 0) {
    buffer.length = 0;
    return;
  }
  const events = buffer.splice(0, buffer.length);
  try {
    const body = JSON.stringify({ events });
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(endpoint, blob);
    } else {
      await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      });
    }
  } catch (e) {
    console.warn("Failed to flush events", e);
  }
}
